import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import z from "zod";

import { CodeVerification } from "~/common/components";
import FormLayout from "~/common/components/layouts/FormLayout";
import { DEFAULT_CODE_LENGTH } from "~/common/constants/verificationCode";
import { useCodeVerificationCooldown } from "~/common/hooks/useCodeVerificationCooldown";
import {
  CodeVerificationSession,
  PhoneNumber,
} from "~/common/types/verificationCode";
import { formatPhoneNumber } from "../utils";

const loginPhoneVerificationSchema = z.object({
  code: z
    .string("Verification code is required")
    .min(6, `${DEFAULT_CODE_LENGTH} character code required`),
});

type LoginPhoneVerification = z.infer<typeof loginPhoneVerificationSchema>;

export const PhoneVerificationCode = ({
  verificationSession,
  phoneNumber,
  isLoading,
  onSubmit,
  onRetry,
  onCancel,
}: {
  verificationSession: CodeVerificationSession;
  isLoading: boolean;
  phoneNumber: PhoneNumber;
  onSubmit: SubmitHandler<LoginPhoneVerification>;
  onRetry: () => void;
  onCancel: () => void;
}) => {
  const { cooldown } = useCodeVerificationCooldown(
    verificationSession.canResendAt,
  );

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<LoginPhoneVerification>({
    resolver: zodResolver(loginPhoneVerificationSchema),
  });

  return (
    <FormLayout
      handleSubmit={handleSubmit(onSubmit)}
      title="Verification code"
      description={`A security code was sent to ${formatPhoneNumber(phoneNumber.number ?? "")}. Enter it below to confirm your login.`}
      backLabel="Back"
      submitLabel="Continue"
      handleGoBack={onCancel}
      pendingRequest={isLoading}
    >
      <Controller
        name="code"
        control={control}
        render={({ field }) => {
          return (
            <CodeVerification
              value={field.value}
              disabled={isLoading}
              onChange={field.onChange}
              onRetry={onRetry}
              error={errors.code?.message}
              remainingTime={cooldown.remainingTime}
              inCooldownTime={cooldown.active}
            />
          );
        }}
      />
    </FormLayout>
  );
};
